import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { HTTP_METHOD } from './_shared';

const ENDPOINT = '/api/v2/shop/product-variants/{code}';
export type MakeGetProductVariantByCodeDeps<ProductVariantShow extends SchemaType.Top> = {
  readonly schemas: {
    readonly productVariantShow: ProductVariantShow;
  };
  readonly adapters: {
    readonly productVariantShow: {
      readonly inStock: (productVariant: ProductVariantShow['Type']) => boolean | undefined;
    };
  };
  readonly rawUrlEncode: (input: string) => string;
};

export const makeGetProductVariantByCode = <ProductVariantShow extends SchemaType.Top>(
  deps: MakeGetProductVariantByCodeDeps<ProductVariantShow>
) => {
  const GetProductVariantByCodeError = createBackendApiError({
    domain: 'ProductVariant',
    endpoint: 'GetByCode',
    path: ENDPOINT,
    identifierField: 'code',
  });

  const getProductVariantByCodeFetch = (code: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ params: { code: deps.rawUrlEncode(code) } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'productVariant.code': code,
        },
      })
    );

  const getProductVariantByCode = (code: string) =>
    getProductVariantByCodeFetch(code).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.schemas.productVariantShow)(response),
          404: (response) =>
            Effect.fail(new GetProductVariantByCodeError.NotFoundError({ code, response })),
          orElse: (response) =>
            Effect.fail(new GetProductVariantByCodeError.ApiRequestError({ code, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductVariantGet,
          'Debug',
          `Successfully resolved product variant for code: ${code} -> inStock: ${String(deps.adapters.productVariantShow.inStock(result))}`
        )
      ),
      Effect.withSpan('api.productVariant.get', {
        kind: 'internal',
        attributes: {
          'productVariant.code': code,
        },
      })
    );

  return { getProductVariantByCode, GetProductVariantByCodeError };
};
